"use client"

import { Button } from "@/elements"
import clsx from "clsx"
import React, { useEffect, useState } from "react"
import { Dialog, DialogContent, DialogFooter } from "../../Dialog"
import { StepOne } from "./components/StepOne"
import { StepTwo } from "./components/StepTwo"
import { SubscriptionSuccessStep } from "./components/SubscriptionSuccessStep"
import { useSubscriptionCheckoutModal } from "./useSubscriptionCheckoutModal"
import { useQuery } from "@apollo/client"
import { graphql } from "@/gql"
import { DayPassSuccessStep } from "./components/DayPassSuccessStep"
import { ampli } from "@/ampli"

export type CheckoutModalEntryPoint =
  | "apply-to-job"
  | "shortcut-after-profile-setup"
  | "job-detail-page"
  | "job-board"
  | "job-board-filters"
  | "my-applications"
  | "invited-to-a-job"
  | "job-recommendations-batch"
  | "job-recommendation-detail-page"
  | "my-stuff"
  | "contact-info"
  | "apply-to-jobs-shortcut-profile-setup"
  | "my-invitations"
  | "apply-to-invited-job"
interface Props {
  open: boolean
  onOpenChange?: (open: boolean) => void
  onClose?: () => void
  continueApplyingJobPostingId?: string | null
  entryPoint: CheckoutModalEntryPoint
}

const SubscriptionCheckoutSuccessContinueApplyingQuery = graphql(`
  query SubscriptionCheckoutSuccessContinueApplyingQuery($jobPostingId: ID!) {
    jobPosting(id: $jobPostingId) {
      id
      ...SubscriptionCheckoutSuccess
    }
  }
`)

/*
 * Note: onOpenChange is optional and should only be passed if the Modal can be freely opened and closed
 * in some contexts we only allow the modal to be closed with onClose prop and not closed by clicking outside of the modal
 */
export const SubscriptionCheckoutModal: React.FC<Props> = ({
  onOpenChange,
  open,
  continueApplyingJobPostingId,
  entryPoint,
}) => {
  const {
    subscriptionPlans,
    step,
    onChangeStep,
    onContinueWithPlan,
    handleCheckoutClose,
    selectedPlanId,
    onSelectPlan,
    singlePurchasePlans,
    eligibleForFreeTrial,
    selectedProductType,
    setSelectedProductType,
  } = useSubscriptionCheckoutModal(entryPoint)

  const dayPassSelected = selectedProductType === "daily"

  const [isModalLocked, setIsModalLocked] = useState(false)

  const { data: jobPostingData } = useQuery(SubscriptionCheckoutSuccessContinueApplyingQuery, {
    variables: {
      jobPostingId: continueApplyingJobPostingId!,
    },
    skip: Boolean(!continueApplyingJobPostingId),
    context: {
      version: 2,
    },
  })

  useEffect(() => {
    if (open) {
      ampli.planSelectionScreenView({
        entry_point: entryPoint,
      })
    }
  }, [open, entryPoint])

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isModalLocked) {
        e.preventDefault()
        e.returnValue = ""
        return ""
      }
    }

    window.addEventListener("beforeunload", handleBeforeUnload)

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload)
    }
  }, [isModalLocked])

  const handleOnOpenChange = (open: boolean) => {
    if (!open && isModalLocked) {
      return
    }
    if (Boolean(onOpenChange)) {
      if (!open) {
        handleCheckoutClose()
      }

      onOpenChange?.(open)
    }
  }

  const onClose = () => {
    if (Boolean(onOpenChange)) {
      onOpenChange?.(false)
      handleCheckoutClose()
    } else {
      onClose()
    }
  }

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <StepOne
            subscriptionPlans={subscriptionPlans}
            singlePurchasePlans={singlePurchasePlans}
            onSelectPlan={onSelectPlan}
            selectedPlanId={selectedPlanId}
            eligibleForFreeTrial={eligibleForFreeTrial}
            closeModal={() => onOpenChange?.(false)}
          />
        )
      case 2:
        return (
          <StepTwo
            subscriptionPlans={subscriptionPlans}
            singlePurchasePlans={singlePurchasePlans}
            selectedPlanId={selectedPlanId}
            onClickBack={() => {
              if (isModalLocked) {
                return
              }
              onChangeStep(1)
            }}
            setStep={onChangeStep}
            selectedProductType={selectedProductType}
            eligibleForFreeTrial={eligibleForFreeTrial}
            setIsModalLocked={setIsModalLocked}
            setSelectedProductType={setSelectedProductType}
          />
        )
      case 3:
        switch (selectedProductType) {
          case "daily":
            return <DayPassSuccessStep onClose={onClose} />
          default:
            return <SubscriptionSuccessStep onClose={onClose} jobPosting={jobPostingData?.jobPosting} />
        }
      default:
        return null
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOnOpenChange}>
      <DialogContent
        className={clsx("sm:rounded-xl", step === 2 && "md:w-[534px] lg:w-[851px]")}
        hasMaxWidth={false}
        withClose={false}>
        <div
          className={clsx(
            "relative h-full w-full overflow-y-scroll bg-bg-1 sm:h-auto",
            step === 1 && "max-h-screen px-8 py-8 sm:max-h-[700px] sm:max-w-[540px] lg:px-10 lg:py-10",
            step === 2 &&
              "max-h-screen px-8 py-7 sm:max-h-[calc(100dvh-112px)] lg:max-h-[710px] lg:max-w-[850px] lg:px-0",
            step === 3 && "max-h-screen sm:w-[484px]"
          )}>
          {renderStep()}
        </div>
        {step === 1 && (
          <>
            <div className="hidden sm:block">
              <DialogFooter
                primaryButton={{
                  children: eligibleForFreeTrial && !dayPassSelected ? "Start free trial" : "Get Pro",
                  onClick: onContinueWithPlan,
                  disabled: !selectedPlanId,
                  variant: "pro-gradient",
                }}
                secondaryButton={{
                  children: "I just want to post jobs",
                  onClick: onClose,
                }}
              />
            </div>
            <div className="sm:hidden">
              <div className="relative bottom-0 z-10 flex h-fit flex-col justify-between gap-2 border-t border-bg-3 bg-bg-1 py-4 pl-5 pr-8">
                <Button variant="pro-primary" onClick={onContinueWithPlan}>
                  {eligibleForFreeTrial && !dayPassSelected ? "Start free trial" : "Get Pro"}
                </Button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
